// src/screens/MerchantStarDetailScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import './MerchantStarDetailScreen.css';
import backIcon from '../assets/backspace-icon.png';

const pretty = (n) => Number(n || 0).toFixed(6).replace(/\.?0+$/, '');

function normalizeWindowLabel(rawWindow, fallbackWindowParam = '60d') {
  const key = String(rawWindow || fallbackWindowParam || '60d').toLowerCase();

  if (key === '7d') return '7D';
  if (key === '30d') return '30D';
  if (key === '60d') return '60D';
  if (key === '90d') return '90D';

  return '60D';
}

export default function MerchantStarDetailScreen() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const windowParam = searchParams.get('window') || '60d';

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [lbRow, setLbRow] = useState(null);

  const [metrics, setMetrics] = useState({ avgRating: 0, completedOrders: 0 });
  const [metricsLoading, setMetricsLoading] = useState(false);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setErr(null);

        const lbRes = await api.get('/badges/leaderboard?type=MERCHANT_STAR&limit=200');
        const items = Array.isArray(lbRes?.data?.items)
          ? lbRes.data.items
          : Array.isArray(lbRes?.data)
          ? lbRes.data
          : [];

        const mine = (items || []).find((r) => r.username === username) || null;
        setLbRow(mine);

        try {
          setMetricsLoading(true);
          const mRes = await api.get(`/merchants/metrics/${username}`);
          setMetrics({
            avgRating: Number(mRes.data?.avgRating ?? 0),
            completedOrders: Number(mRes.data?.completedOrders ?? 0),
          });
        } catch (e) {
          setMetrics({ avgRating: 0, completedOrders: 0 });
        } finally {
          setMetricsLoading(false);
        }
      } catch (e) {
        setErr(e?.response?.data?.error || e?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [username]);

  const windowLabel = useMemo(() => {
    return normalizeWindowLabel(lbRow?.meta?.window, windowParam);
  }, [lbRow?.meta?.window, windowParam]);

  const since = lbRow?.meta?.since ? new Date(lbRow.meta.since) : null;
  const until = lbRow?.meta?.until ? new Date(lbRow.meta.until) : null;

  return (
    <div className="msd-page">
      {/* ✅ ISOLATED HEADER */}
      <div className="msd-topbar">
        <img
          src={backIcon}
          className="msd-topbar-back"
          alt="Back"
          onClick={() => navigate(-1)}
        />
        <div className="msd-topbar-title">Merchant Star</div>
      </div>

      <div className="msd-scroll">
        {loading ? (
          <div className="msd-info">Loading…</div>
        ) : err ? (
          <div className="msd-error">{String(err)}</div>
        ) : !lbRow ? (
          <div className="msd-info">
            @{username} is not in the current list.
          </div>
        ) : (
          <>
            {/* Identity */}

<div className="msd-user">
  <img
    className="msd-avatar"
    src={lbRow.profilePic || '/placeholder-profile.png'}
    alt={lbRow.username}
  />
  <div className="msd-user-meta">
    <div className="msd-username">@{lbRow.username}</div>
    <div className="msd-name">{lbRow.name || lbRow.username}</div>
  </div>
  <button
    type="button"
    className="msd-view-profile"
    onClick={() => navigate(`/user/${encodeURIComponent(lbRow.username)}`)}
  >
    View profile
  </button>
</div>
            {/* Totals */}
            <div className="msd-card">
              <div className="msd-window">
                <strong>Last {windowLabel.replace('D', '')} days</strong>
                {since && until && (
                  <span className="msd-window-range">
                    {' '}• {since.toLocaleString()} → {until.toLocaleString()}
                  </span>
                )}
              </div>

              <div className="msd-rank-top">Rank #{lbRow.rank}</div>

              <div className="msd-row">
                <div className="msd-k">Total Sales</div>
                <div className="msd-v">
                  {pretty(lbRow.meta?.sales ?? lbRow.score ?? 0)} Pi
                </div>
              </div>

              <div className="msd-row">
                <div className="msd-k">Last Order</div>
                <div className="msd-v">
                  {lbRow.earnedAt
                    ? new Date(lbRow.earnedAt).toLocaleString()
                    : '—'}
                </div>
              </div>
            </div>

            {/* Store metrics */}
            <div className="msd-card">
              <div className="msd-section-title">Store metrics</div>

              <div className="msd-row">
                <div className="msd-k">Average Rating</div>
                <div className="msd-v">
                  {metricsLoading ? '…' : `${metrics.avgRating.toFixed(1)} ★`}
                </div>
              </div>

              <div className="msd-row">
                <div className="msd-k">Completed Orders</div>
                <div className="msd-v">
                  {metricsLoading ? '…' : metrics.completedOrders}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
